/* eslint-disable @typescript-eslint/no-explicit-any */
import { useShapeStore } from '@/app/data/Editor/shape'
import { useDefectPhotoStore } from '@/app/data/Editor/defectPhoto'
import { useFacilityPhotoStore } from '@/app/data/Editor/facilityPhoto'
import { useQualityInspectionStore } from '@/app/data/Editor/qualityInspection'
import { useQualityResultStore } from '@/app/data/Editor/qualityResult'
import { useQualityResultPhotoStore } from '@/app/data/Editor/qualityResultPhoto'
import { useDeviceEventLogStore, DEVICE_EVENT_LOG_ENABLED } from '@/app/store/deviceEventLog'
import type { ISyncableStore } from './types'
import { getItemToProcess } from '../util'
import { SQL, APP_TABLE_NAMES, AppTableName } from '@/lib/Sqlite'
import { Network } from '@capacitor/network'
import { APIServer } from '@/app/data/api'
import { syncQueue } from '@/app/sync'
import { prepareSQLiteValue } from '@/lib/Sqlite/schema'
import SQLite from '@/lib/Task/sqlite'
import { isSyncSuspended } from './suspend'
import {
  Defect,
  DefectPhoto,
  Floorplan,
  FacilityPhoto,
  QualityInspection,
  QualityInspectionResult,
  QualityResultPhoto,
  SyncableDataType,
} from '../data/types'
import Merge from '../../lib/Task/merge'
import { SQLiteEventBus } from '@/lib/sqliteEventBus'
import { useAuthStore } from '@/app/data/auth'
import { readFreshRowState, getSnapshotStaleReason } from './mergeWriteGuard'
import { DEFECT_REJECTED_SQL, pendingExtraSql } from './syncRejection'

/**
 * SQLite row 타입 (JSON 파싱 전)
 */
interface SQLiteRow {
  [key: string]: string | number | null
}

/**
 * 앱의 모든 미동기화 데이터를 찾아 SyncQueue에 작업을 추가하는 중앙 관리 클래스.
 */
export class SyncManager {
  // Promise 기반 락 (race condition 방지)
  private static syncLock: Promise<void> | null = null
  private static syncLockResolve: (() => void) | null = null

  // 동기화 중단 플래그 (로그아웃 시 사용)
  private static shouldAbortSync = false

  // taskId별 동기화 상태 추적
  // 'pending' | 'syncing' | 'completed'
  private static taskSyncStatus: Map<string, 'pending' | 'syncing' | 'completed'> = new Map()

  // taskId별 마지막 동기화 시간
  private static lastSyncTime: Map<string, number> = new Map()

  /**
   * 특정 taskId가 현재 동기화 중인지 확인
   * @param taskId - 확인할 taskId
   * @returns 동기화 중 여부 (completed 상태는 false 반환)
   */
  public static isTaskSyncing(taskId: string): boolean {
    const status = this.taskSyncStatus.get(taskId)

    // 1. 동기화 상태가 'syncing'인 경우
    if (status === 'syncing') {
      return true
    }

    // 2. 큐에 해당 taskId의 작업이 대기 중인 경우 (가장 정확한 체크)
    const queuedJobCount = syncQueue.getInProgressJobCountByTaskId(taskId)
    if (queuedJobCount > 0) {
      console.info(
        `[SyncManager] taskId ${taskId}: 큐에 ${queuedJobCount}개 작업 대기 중 - syncing 상태 유지`,
      )
      return true
    }

    // 3. completed 상태는 동기화 완료를 의미하므로 false 반환
    // pending 또는 undefined도 false 반환
    return false
  }

  /**
   * taskId의 동기화 상태를 설정
   */
  private static setTaskSyncStatus(
    taskId: string,
    status: 'pending' | 'syncing' | 'completed',
  ): void {
    this.taskSyncStatus.set(taskId, status)
    if (status === 'completed') {
      this.lastSyncTime.set(taskId, Date.now())
    }
  }

  // 진행 중인 동기화 사이클만 중단 (상태/큐는 보존 - 매뉴얼 데모 진입 등 일시정지용)
  public static abortRunningSync(): void {
    this.shouldAbortSync = true
  }

  /**
   * 모든 동기화 상태 초기화 (로그아웃 시 호출)
   */
  public static clearSyncStatus(): void {
    // 진행 중인 동기화 중단 플래그 설정
    this.shouldAbortSync = true
    // 락 해제
    if (this.syncLockResolve) {
      this.syncLockResolve()
      this.syncLock = null
      this.syncLockResolve = null
    }
    this.taskSyncStatus.clear()
    this.lastSyncTime.clear()
    // 진행중이던 항목도 강제로 취소시킵니다.
    syncQueue.clearQueue()
  }

  // 미동기화 집계 대상 테이블 (storesToSync와 동일한 범위, device_event_log 제외)
  private static readonly PENDING_COUNT_TABLES = [
    'defects',
    'defect_photos',
    'facility_photos',
    'quality_inspections',
    'quality_inspection_results',
    'quality_result_photos',
  ]

  // 품질점검 동기화 대상 테이블 - task.type === 'QUALITY'인 과업만 동기화 게이트
  private static readonly QUALITY_STORE_KEYS = new Set([
    'quality_inspections',
    'quality_inspection_results',
    'quality_result_photos',
  ])

  // QUALITY 판정 캐시 (true만 캐시 - tasks row 미적재 시점의 오판 고착 방지)
  private static qualityTaskIdCache = new Set<string>()

  /**
   * 해당 task가 품질점검(QUALITY) 과업인지 로컬 tasks 테이블에서 판정
   * @param taskId 대상 task ID
   * @returns QUALITY 과업 여부 (판정 불가 시 false → 동기화 제외)
   */
  private static async isQualityTask(taskId: string): Promise<boolean> {
    if (!taskId) return false
    if (this.qualityTaskIdCache.has(taskId)) return true
    try {
      if (!SQL.createdTables.has('tasks')) return false
      const db = await SQL.getDatabase()
      const result = await db.query('SELECT type FROM tasks WHERE id = ?', [taskId])
      const isQuality = result.values?.[0]?.type === 'QUALITY'
      if (isQuality) this.qualityTaskIdCache.add(taskId)
      return isQuality
    } catch (error) {
      console.warn(`[SyncManager] QUALITY 과업 판정 실패 (taskId: ${taskId}):`, error)
      return false
    }
  }

  /**
   * 품질점검 테이블이면 QUALITY 과업의 taskId만 남긴다 (기존 SAFETY/DEFECT 영향 없음)
   * @param storeKey 대상 테이블
   * @param taskIds 후보 taskId 목록
   */
  private static async filterQualityTaskIds(
    storeKey: string,
    taskIds: string[],
  ): Promise<string[]> {
    if (!this.QUALITY_STORE_KEYS.has(storeKey)) return taskIds
    const filtered: string[] = []
    for (const taskId of taskIds) {
      if (await this.isQualityTask(taskId)) filtered.push(taskId)
    }
    return filtered
  }

  /**
   * SQLite 기준 미동기화(modifiedFromLocal=1) 항목 수를 반환
   * 인메모리 큐는 앱 재시작 시 사라지므로, UI 대기 건수는 이 값을 기준으로 해야 함
   * @param taskId 대상 task ID
   * @returns 미동기화 항목 수
   */
  public static async getPendingCountByTaskId(taskId: string): Promise<number> {
    return (await this.getSyncCountsByTaskId(taskId)).pending
  }

  // 테이블별 "저장 대기" 조건 - 서버 검증 거절 표식 결함은 대기가 아니라 실패로 따로 집계하고,
  // 생성 거절된 부모에 달린 사진·자식 결함은 어느 쪽에도 세지 않는다 (부모를 다시 저장하면 함께 해결)
  private static pendingWhere(table: string): string {
    return `modifiedFromLocal = 1 AND task_id = ?${this.pendingExtra(table)}`
  }

  private static pendingExtra(table: string): string {
    return pendingExtraSql(table, SQL.createdTables.has('defects'))
  }

  /**
   * SQLite 기준 미동기화 항목 수(pending)와 서버 검증 거절 결함 수(rejected)를 한 번에 반환
   * @param taskId 대상 task ID
   */
  public static async getSyncCountsByTaskId(
    taskId: string,
  ): Promise<{ pending: number; rejected: number }> {
    if (!taskId) return { pending: 0, rejected: 0 }
    try {
      const db = await SQL.getDatabase()
      // 플러그인이 커넥션 1개로 모든 문장을 직렬 실행하므로 테이블별 연쇄 호출 대신
      // 단일 쿼리로 집계 - 배지 폴링이 네이티브 큐를 점유하는 시간을 최소화
      const tables = this.PENDING_COUNT_TABLES.filter(table => SQL.createdTables.has(table))
      if (tables.length === 0) return { pending: 0, rejected: 0 }
      const subQueries = tables
        .map(table => `(SELECT COUNT(*) FROM ${table} WHERE ${this.pendingWhere(table)})`)
        .join(' + ')
      const hasDefects = tables.includes('defects')
      const rejectedQuery = hasDefects
        ? `(SELECT COUNT(*) FROM defects WHERE modifiedFromLocal = 1 AND task_id = ? AND ${DEFECT_REJECTED_SQL})`
        : '0'
      const result = await db.query(`SELECT ${subQueries} AS cnt, ${rejectedQuery} AS rejected`, [
        ...tables.map(() => taskId),
        ...(hasDefects ? [taskId] : []),
      ])
      return {
        pending: Number(result.values?.[0]?.cnt ?? 0),
        rejected: Number(result.values?.[0]?.rejected ?? 0),
      }
    } catch (error) {
      // 구버전 동적 테이블에 컬럼이 없으면 통합 쿼리 전체가 실패할 수 있으므로
      // 테이블별 개별 집계로 폴백 (문제 테이블만 제외, 거절 수는 0으로 둔다)
      console.warn('[SyncManager] 통합 미동기화 집계 실패, 테이블별 폴백:', error)
      let total = 0
      try {
        const db = await SQL.getDatabase()
        for (const table of this.PENDING_COUNT_TABLES) {
          if (!SQL.createdTables.has(table)) continue
          try {
            const result = await db.query(
              `SELECT COUNT(*) as cnt FROM ${table} WHERE ${this.pendingWhere(table)}`,
              [taskId],
            )
            total += Number(result.values?.[0]?.cnt ?? 0)
          } catch {
            // 테이블 구조 상이 등은 집계에서 제외
          }
        }
      } catch (fallbackError) {
        console.error('[SyncManager] 미동기화 집계 실패:', fallbackError)
      }
      return { pending: total, rejected: 0 }
    }
  }

  // SQLite 기준 미동기화 항목이 있는 고유 task 수를 반환 (전역 인디케이터용)
  public static async getPendingTaskCount(): Promise<number> {
    try {
      const db = await SQL.getDatabase()
      // 단일 쿼리 집계 (getPendingCountByTaskId와 동일한 사유)
      const tables = this.PENDING_COUNT_TABLES.filter(table => SQL.createdTables.has(table))
      if (tables.length === 0) return 0
      const unionQuery = tables
        .map(
          table =>
            `SELECT task_id FROM ${table} WHERE modifiedFromLocal = 1${this.pendingExtra(table)}`,
        )
        .join(' UNION ')
      const result = await db.query(
        `SELECT COUNT(DISTINCT task_id) AS cnt FROM (${unionQuery}) WHERE task_id IS NOT NULL AND task_id != 0 AND task_id != ''`,
      )
      return Number(result.values?.[0]?.cnt ?? 0)
    } catch (error) {
      // 구버전 동적 테이블 컬럼 상이 시 테이블별 개별 집계로 폴백
      console.warn('[SyncManager] 통합 미동기화 과업 집계 실패, 테이블별 폴백:', error)
      const taskIds = new Set<string>()
      try {
        const db = await SQL.getDatabase()
        for (const table of this.PENDING_COUNT_TABLES) {
          if (!SQL.createdTables.has(table)) continue
          try {
            const result = await db.query(
              `SELECT DISTINCT task_id FROM ${table} WHERE modifiedFromLocal = 1${this.pendingExtra(table)}`,
            )
            for (const row of result.values ?? []) {
              const id = (row as SQLiteRow).task_id
              if (id && id !== 0 && id !== '') taskIds.add(String(id))
            }
          } catch {
            // 테이블 구조 상이 등은 집계에서 제외
          }
        }
      } catch (fallbackError) {
        console.error('[SyncManager] 미동기화 과업 집계 실패:', fallbackError)
      }
      return taskIds.size
    }
  }

  /**
   * 로컬 데이터에서 고유한 taskId 목록을 추출합니다.
   * @param storeKey 대상 테이블
   * @param onlyModified true면 미동기화(modifiedFromLocal=1) 항목이 있는 task만 반환
   *   - 백그라운드 사이클은 push가 목적이므로 이 모드를 사용 (server-pull은 initData가 담당)
   */
  private static async getUniqueTaskIds(
    storeKey: string,
    onlyModified: boolean = false,
  ): Promise<string[]> {
    try {
      const authStore = useAuthStore()
      const companyId = authStore.companyId
      const db = await SQL.getDatabase()
      let sql = `SELECT DISTINCT task_id FROM ${storeKey}`
      const params: (string | number)[] = []
      const conditions: string[] = []
      if (onlyModified) {
        // 서버 검증 거절 표식 결함(과 거기 달린 사진·자식 결함)만 남은 과업은 주기 동기화 대상에서 뺀다
        // (보낼 것이 없는데 매 주기 GET·병합하지 않도록)
        conditions.push(`modifiedFromLocal = 1${this.pendingExtra(storeKey)}`)
      }
      // 현재 로그인된 계정의 task만 동기화 (다른 계정 데이터 보호)
      // 로컬 생성 행(company_id 0/NULL)은 현재 계정 소유로 간주해 포함하고,
      // company_id 컬럼이 없는 동적 생성 테이블에는 필터를 적용하지 않음
      const tableInfo = await db.query(`PRAGMA table_info(${storeKey})`)
      const hasCompanyIdColumn =
        tableInfo.values?.some((col: SQLiteRow) => col.name === 'company_id') ?? false
      if (companyId && hasCompanyIdColumn) {
        conditions.push(
          // eslint-disable-next-line quotes
          `(company_id = ? OR company_id = 0 OR company_id IS NULL OR company_id = '')`,
        )
        params.push(companyId)
      }
      if (conditions.length > 0) {
        sql += ` WHERE ${conditions.join(' AND ')}`
      }
      const result = await db.query(sql, params)
      if (!result.values || result.values.length === 0) return []
      return result.values
        .map((row: SQLiteRow) => row.task_id)
        .filter((id: string | number | null) => id && id !== '' && id !== 0)
        .map((id: string | number) => String(id))
    } catch (error) {
      console.error(`[SyncManager] taskId 목록 추출 실패 (${storeKey}):`, error)
      return []
    }
  }

  /**
   * 특정 taskId의 서버 데이터를 가져옵니다.
   */
  private static async getServerDataByType(
    storeKey: string,
    taskId: string,
  ): Promise<SyncableDataType[] | null> {
    try {
      switch (storeKey) {
        case 'defects': {
          const data: Defect[] = await APIServer.getShapeListByTaskId(Number(taskId))
          return data
            .filter((item: Defect) => !item.deleted_at)
            .map(
              (item: Defect): Defect => ({
                ...item,
                localStringId: item.id.toString(),
                modifiedFromLocal: false,
              }),
            )
        }

        case 'defect_photos': {
          const data: DefectPhoto[] = await APIServer.getDefectPhotoListByTaskId(taskId)
          return data
            .filter((item: DefectPhoto) => !item.deleted_at)
            .map(
              (item: DefectPhoto): DefectPhoto => ({
                ...item,
                localStringId: item.id.toString(),
                modifiedFromLocal: false,
                localShapeId: item.defect_id?.toString() || '',
              }),
            )
        }

        case 'facility_photos': {
          const data: FacilityPhoto[] = await APIServer.getFacilityBranchPhotoListByTaskId(taskId)
          return data
            .filter((item: FacilityPhoto) => !item.deleted_at)
            .map(
              (item: FacilityPhoto): FacilityPhoto => ({
                ...item,
                localStringId: item.id.toString(),
                modifiedFromLocal: false,
              }),
            )
        }

        case 'floorplans': {
          const data: Floorplan[] = await APIServer.getFloorplanByTaskId(taskId)
          return data
            .filter((item: Floorplan) => !item.deleted_at)
            .map(
              (item: Floorplan): Floorplan => ({
                ...item,
                localStringId: item.id.toString(),
                modifiedFromLocal: false,
              }),
            )
        }

        case 'quality_inspections': {
          const data: QualityInspection[] = await APIServer.getQualityInspectionListByTaskId(
            Number(taskId),
          )
          return data
            .filter((item: QualityInspection) => !item.deleted_at)
            .map(
              (item: QualityInspection): QualityInspection => ({
                ...item,
                localStringId: item.id.toString(),
                modifiedFromLocal: false,
              }),
            )
        }

        case 'quality_inspection_results': {
          const data: QualityInspectionResult[] = await APIServer.getQualityResultListByTaskId(
            Number(taskId),
          )
          return data
            .filter((item: QualityInspectionResult) => !item.deleted_at)
            .map(
              (item: QualityInspectionResult): QualityInspectionResult => ({
                ...item,
                localStringId: item.id.toString(),
                modifiedFromLocal: false,
              }),
            )
        }

        case 'quality_result_photos': {
          const data: QualityResultPhoto[] = await APIServer.getQualityResultPhotoListByTaskId(
            Number(taskId),
          )
          // result_id NULL(FK SET NULL 잔존 행)은 연결 대상이 없어 제외 (스토어 getServerData와 동일 규칙)
          return data
            .filter((item: QualityResultPhoto) => !item.deleted_at && item.result_id !== null)
            .map(
              (item: QualityResultPhoto): QualityResultPhoto => ({
                ...item,
                localStringId: item.id.toString(),
                // 서버형 row의 로컬 연결키는 서버 result id로 백필 (defect_photos의 localShapeId와 동일)
                localResultId: item.result_id?.toString() || '',
                modifiedFromLocal: false,
              }),
            )
        }

        default:
          console.warn(`[SyncManager] 지원하지 않는 storeKey: ${storeKey}`)
          return null
      }
    } catch (error) {
      console.error(
        `[SyncManager] 서버 데이터 가져오기 실패 (${storeKey}, taskId: ${taskId}):`,
        error,
      )
      return null
    }
  }

  /**
   * taskId별로 로컬 데이터를 가져옵니다.
   */
  private static async getLocalDataByTaskId(
    storeKey: string,
    taskId: string,
  ): Promise<SyncableDataType[]> {
    try {
      const db = await SQL.getDatabase()
      const result = await db.query(`SELECT * FROM ${storeKey} WHERE task_id = ?`, [taskId])
      if (!result.values || result.values.length === 0) return []
      const parsedRows: SyncableDataType[] = []
      let rowIndex = 0
      for (const row of result.values as SQLiteRow[]) {
        // 대형 task에서 JSON 파싱 루프가 메인스레드를 장시간 점유하지 않도록 주기적으로 양보
        rowIndex++
        if (rowIndex % 300 === 0) {
          await new Promise(resolve => setTimeout(resolve, 0))
        }
        // JSON 필드 파싱 + iOS "557.0" 정규화 (initData 경로와 동일 규칙 유지)
        // 자체 파싱 시 id가 "557.0"으로 남아 서버 매칭이 실패 → 살아있는 row가 삭제 처리됨
        parsedRows.push(SQLite.parseRow(row, storeKey) as SyncableDataType)
      }
      return parsedRows
    } catch (error) {
      console.error(
        `[SyncManager] 로컬 데이터 가져오기 실패 (${storeKey}, taskId: ${taskId}):`,
        error,
      )
      return []
    }
  }

  /**
   * 동기화 중단 여부 확인
   * @returns 중단해야 하면 true
   */
  private static checkAbort(): boolean {
    if (this.shouldAbortSync) {
      console.info('[SyncManager] 동기화 중단 요청됨')
      return true
    }
    return false
  }

  /**
   * 동기화 락 획득 (race condition 방지)
   * @returns 락 획득 성공 여부
   */
  private static async acquireLock(): Promise<boolean> {
    // 이미 락이 있으면 대기하지 않고 즉시 반환 (중복 실행 방지)
    if (this.syncLock !== null) {
      console.warn('[SyncManager] 전체 동기화 이미 진행중 - 건너뜀')
      return false
    }

    // 새 락 생성
    this.syncLock = new Promise<void>(resolve => {
      this.syncLockResolve = resolve
    })
    return true
  }

  /**
   * 동기화 락 해제
   */
  private static releaseLock(): void {
    if (this.syncLockResolve) {
      this.syncLockResolve()
      this.syncLock = null
      this.syncLockResolve = null
    }
  }

  public static async syncAllPendingData(): Promise<void> {
    // 매뉴얼 데모 API 핸들러 설치 중에는 서버 요청이 데모 응답으로 대체되므로 동기화 금지
    // (업로드가 성공 처리되며 modifiedFromLocal이 해제 → 서버 미반영 데이터 유실)
    if (isSyncSuspended()) {
      console.warn('[SyncManager] 동기화 일시정지 상태 - 건너뜀')
      return
    }

    // 락 획득 시도
    const acquired = await this.acquireLock()
    if (!acquired) {
      console.warn('[SyncManager] 락 획득 실패 - 동기화 건너뜀')
      return
    }

    // 동기화 시작 전 중단 플래그 초기화
    this.shouldAbortSync = false
    // 진단용: 사이클 소요시간 측정 (장시간 사용 시 성능 저하 추적)
    const syncCycleStartedAt = performance.now()
    console.info('[SyncManager] 🚀 전체 동기화 시작')

    try {
      // 네트워크 연결 확인
      const isConnected = (await Network.getStatus()).connected
      if (!isConnected) {
        console.warn('[SyncManager] 오프라인 상태 - 동기화 건너뜀')
        return
      }

      // 버그 복구: merge가 잘못 soft-delete한 _need_to_upload 레코드 복원
      try {
        await this.recoverBugDeletedRecords()
      } catch (e) {
        console.error('[SyncManager] Bug recovery failed:', e)
      }

      // 중단 확인
      if (this.checkAbort()) return

      const storesToSync: { name: string; store: ISyncableStore; storeKey: string }[] = [
        { name: 'Defects', store: useShapeStore() as ISyncableStore, storeKey: 'defects' },
        {
          name: 'DefectPhotos',
          store: useDefectPhotoStore() as ISyncableStore,
          storeKey: 'defect_photos',
        },
        {
          name: 'FacilityPhotos',
          store: useFacilityPhotoStore() as ISyncableStore,
          storeKey: 'facility_photos',
        },
        // 품질점검 스토어 - taskId별 QUALITY 과업 게이트(filterQualityTaskIds)를 통과한 것만 동기화
        {
          name: 'QualityInspections',
          store: useQualityInspectionStore() as ISyncableStore,
          storeKey: 'quality_inspections',
        },
        {
          name: 'QualityResults',
          store: useQualityResultStore() as ISyncableStore,
          storeKey: 'quality_inspection_results',
        },
        {
          name: 'QualityResultPhotos',
          store: useQualityResultPhotoStore() as ISyncableStore,
          storeKey: 'quality_result_photos',
        },
      ]
      // device_event_log 기능 비활성화 시 sync 대상에서 제외
      // (미동기화 로그가 누적된 단말에서 매 주기 전체 SELECT가 브리지 JSON 직렬화 OOM을 유발)
      if (DEVICE_EVENT_LOG_ENABLED) {
        storesToSync.push({
          name: 'DeviceEventLogs',
          // 스토어는 getAllModifiedData/addApiQueue를 실제로 반환하지만
          // pinia 타입 추론 경계에서 누락되어 unknown 경유 캐스트 필요
          store: useDeviceEventLogStore() as unknown as ISyncableStore,
          storeKey: 'device_event_log',
        })
      }

      // 1단계: 미동기화 항목이 있는 taskId만 수집 (server-pull은 화면 진입 시 initData가 담당)
      const allTaskIds = new Set<string>()
      for (const { storeKey } of storesToSync) {
        // 품질점검 테이블은 QUALITY 과업만 대상 (기존 계정에서는 실행되지 않음)
        const taskIds = await this.filterQualityTaskIds(
          storeKey,
          await this.getUniqueTaskIds(storeKey, true),
        )
        taskIds.forEach(taskId => allTaskIds.add(taskId))
      }

      // 2단계: 수집된 모든 taskId를 syncing 상태로 설정
      if (allTaskIds.size > 0) {
        console.info(`[SyncManager] ${allTaskIds.size}개 taskId를 syncing 상태로 설정`)
        allTaskIds.forEach(taskId => this.setTaskSyncStatus(taskId, 'syncing'))
      }

      // 3단계: 모든 store의 병합 먼저 완료 (SQLite 저장까지)
      // taskId별로 병합된 데이터를 임시 저장
      const mergedDataByStore = new Map<
        string,
        Map<string, { mergedData: SyncableDataType[]; stillModifiedData: SyncableDataType[] }>
      >()

      console.info('[SyncManager] 1단계: 모든 store 병합 시작')
      for (const { name, storeKey } of storesToSync) {
        // 중단 확인
        if (this.checkAbort()) return

        try {
          // device_event_log는 별도 처리 (큐 추가 단계에서 처리)
          if (storeKey === 'device_event_log') {
            continue
          }

          // 1. 미동기화 항목이 있는 taskId 목록 추출 (push 대상만 병합)
          // 품질점검 테이블은 QUALITY 과업만 대상으로 게이트
          const taskIds = await this.filterQualityTaskIds(
            storeKey,
            await this.getUniqueTaskIds(storeKey, true),
          )
          if (taskIds.length === 0) {
            console.info(`[SyncManager] ${name}: 동기화할 데이터 없음`)
            continue
          }

          console.info(`[SyncManager] ${name}: ${taskIds.length}개 task 발견`)

          // store별 데이터 맵 초기화
          if (!mergedDataByStore.has(storeKey)) {
            mergedDataByStore.set(storeKey, new Map())
          }
          const storeDataMap = mergedDataByStore.get(storeKey)!

          // 2. taskId별로 병합 처리
          for (const taskId of taskIds) {
            // 중단 확인
            if (this.checkAbort()) return

            console.info(`[SyncManager] ${name} - taskId ${taskId} 병합 중...`)

            // 3. 로컬 데이터 가져오기
            const localData = await this.getLocalDataByTaskId(storeKey, taskId)
            console.info(`[SyncManager] 로컬 데이터: ${localData.length}개`)

            // 4. 서버 데이터 가져오기
            const serverData = await this.getServerDataByType(storeKey, taskId)
            if (serverData === null) {
              console.warn(`[SyncManager] ${name} - taskId ${taskId}: 서버 데이터 가져오기 실패`)
              // 실패한 경우도 로컬 데이터를 저장
              const modifiedData = localData.filter(
                (item: SyncableDataType) => item.modifiedFromLocal,
              )
              storeDataMap.set(taskId, {
                mergedData: localData,
                stillModifiedData: modifiedData,
              })
              continue
            }

            console.info(`[SyncManager] 서버 데이터: ${serverData.length}개`)

            // 서버가 빈 배열을 반환했지만 로컬에 비삭제 데이터가 있는 경우
            // 백그라운드 싱크의 일시적 서버 장애로 전체 soft-delete되는 것을 방지
            // (initData는 사용자가 직접 열 때 호출되므로 서버 상태를 그대로 반영)
            const activeLocalData = localData.filter((item: SyncableDataType) => !item.deleted_at)
            if (serverData.length === 0 && activeLocalData.length > 0) {
              console.warn(
                `[SyncManager] ${name} - taskId ${taskId}: 서버 빈 배열, 로컬 비삭제 ${activeLocalData.length}개 보호 - 병합 스킵`,
              )
              const modifiedData = localData.filter(
                (item: SyncableDataType) => item.modifiedFromLocal,
              )
              storeDataMap.set(taskId, {
                mergedData: localData,
                stillModifiedData: modifiedData,
              })
              continue
            }

            // 5. 병합
            // mergeBothData의 8단계가 localStringId를 in-place 백필하므로(미매칭 row는 로컬 객체
            // 참조가 그대로 결과에 들어감), 동일-row 스킵 비교용 DB 원본 값을 병합 전에 스냅샷
            const originalLocalStringIds = new Map<unknown, unknown>()
            for (const localItem of localData) {
              originalLocalStringIds.set(
                localItem,
                (localItem as unknown as Record<string, unknown>).localStringId,
              )
            }
            let mergedData = []
            mergedData = Merge.mergeBothData(localData, serverData, storeKey)
            console.info(`[SyncManager] 병합 완료: ${mergedData.length}개`)

            // 5-1. 병합된 데이터를 SQLite에 저장
            try {
              const db = await SQL.getDatabase()

              // 테이블의 실제 컬럼 목록 가져오기 (PRAGMA table_info)
              const tableInfoResult = await db.query(`PRAGMA table_info(${storeKey})`, [])
              const existingColumns = new Set<string>()
              if (tableInfoResult.values) {
                for (const row of tableInfoResult.values) {
                  existingColumns.add(row.name as string)
                }
              }

              // 로컬 row 인덱싱 - 병합 결과가 로컬과 동일하면 재기록을 스킵하기 위함
              const localRowByKey = new Map<string, Record<string, unknown>>()
              for (const localItem of localData) {
                const typedLocal = localItem as unknown as Record<string, unknown>
                const localKey = String(typedLocal.localStringId ?? typedLocal.id ?? '')
                if (localKey) localRowByKey.set(localKey, typedLocal)
              }

              // 병합 중 업로드 완료로 id가 교체된 로컬 row 보호용 - 현재 DB의 id→localStringId
              // (localData 스냅샷은 병합 시작 시점 상태라 그 사이의 id 교체를 반영하지 못함)
              const freshResult = await db.query(
                `SELECT id, localStringId FROM ${storeKey} WHERE task_id = ?`,
                [taskId],
              )
              const freshLocalStringIdById = new Map<string, string>()
              for (const row of (freshResult.values ?? []) as SQLiteRow[]) {
                if (row.id !== undefined && row.id !== null && row.localStringId) {
                  freshLocalStringIdById.set(String(row.id), String(row.localStringId))
                }
              }

              let writtenCount = 0
              let skippedCount = 0
              let comparedCount = 0
              // 동일-스킵 판정용 대표 키 - 서버발 변경은 updated_at/created_at/fields_updated_at에,
              // 병합발 변경은 id/localStringId/modifiedFromLocal/deleted_at에 반드시 반영되므로
              // 이 키들이 모두 같으면 나머지 컬럼도 동일하다.
              // (전 컬럼 비교는 shape_data 등 대형 JSON을 항목마다 직렬화하게 되어 피한다)
              const quickCompareKeys = [
                'id',
                'localStringId',
                'modifiedFromLocal',
                'deleted_at',
                'updated_at',
                'created_at',
                'fields_updated_at',
              ]
              for (const item of mergedData) {
                // 비교/직렬화 루프가 메인스레드를 장시간 점유하지 않도록 주기적으로 양보
                comparedCount++
                if (comparedCount % 200 === 0) {
                  await new Promise(resolve => setTimeout(resolve, 0))
                }
                const typedItem = item as Record<string, unknown>

                // PK(id)가 이미 다른 localStringId의 로컬 row로 점유된 경우(업로드 완료로 id 교체):
                // 서버형 항목으로 덮어쓰면 localShapeId 등 로컬 연결 정보가 파괴되므로 스킵
                const occupiedBy = freshLocalStringIdById.get(String(typedItem.id))
                if (
                  occupiedBy !== undefined &&
                  occupiedBy !== String(typedItem.localStringId ?? '')
                ) {
                  skippedCount++
                  console.warn(
                    `[PhotoGuard] merge 저장 스킵: ${storeKey} id=${typedItem.id} 는 이미 localStringId=${occupiedBy} row가 점유`,
                  )
                  continue
                }

                // 로컬 row와 대표 키가 전부 동일하면 INSERT OR REPLACE 스킵 (쓰기량·직렬화 절감)
                // localStringId는 병합 중 in-place 백필될 수 있으므로 스냅샷한 DB 원본 값과 비교
                const itemKey = String(typedItem.localStringId ?? typedItem.id ?? '')
                const localRow = itemKey ? localRowByKey.get(itemKey) : undefined
                if (
                  localRow &&
                  quickCompareKeys.every(col => {
                    const localValue =
                      col === 'localStringId' ? originalLocalStringIds.get(localRow) : localRow[col]
                    return prepareSQLiteValue(localValue) === prepareSQLiteValue(typedItem[col])
                  })
                ) {
                  skippedCount++
                  continue
                }

                // 실제 테이블에 존재하는 컬럼만 필터링
                const columns = Object.keys(typedItem).filter(
                  col => typedItem[col] !== undefined && existingColumns.has(col),
                )
                // SQLite 호환 형식으로 변환 (객체 → JSON, undefined → null)
                const preparedValues = columns.map(col => prepareSQLiteValue(typedItem[col]))
                const placeholders = columns.map(() => '?').join(', ')

                // 병합 도중 끼어든 사용자 수정 보호: 저장 직전 현재 row를 다시 읽어 스냅샷과 비교하고,
                // 달라졌으면 이번 사이클엔 저장하지 않는다 (스냅샷 되쓰기로 대표사진 변경 등이 되돌아가고
                // modifiedFromLocal이 0으로 내려가 update job이 "이미 동기화됨"으로 스킵되는 lost update 방지)
                if (localRow) {
                  const originalLocalStringId = String(originalLocalStringIds.get(localRow) ?? '')
                  const fresh = await readFreshRowState(
                    storeKey,
                    originalLocalStringId,
                    String(typedItem.id),
                  )
                  const staleReason = fresh ? getSnapshotStaleReason(localRow, fresh) : null
                  if (staleReason) {
                    skippedCount++
                    console.warn(
                      `[PhotoGuard] merge 저장 스킵: ${storeKey} localStringId=${typedItem.localStringId} 병합 중 로컬 수정 감지 (${staleReason})`,
                    )
                    continue
                  }
                }

                // INSERT OR REPLACE 사용
                const sql = `INSERT OR REPLACE INTO ${storeKey} (${columns.join(', ')}) VALUES (${placeholders})`
                await db.run(sql, preparedValues)
                writtenCount++
              }

              // [ADD] 저장 완료 후 대량 업데이트 이벤트 발행 (리스너 완료 대기)
              // 실제로 기록된 row가 없으면 DB가 변하지 않았으므로 발행 생략
              // (리스너가 shapes 배열을 통째로 교체해 캔버스 재정렬을 유발하는 것을 방지)
              if (writtenCount > 0 && APP_TABLE_NAMES.includes(storeKey as any)) {
                await SQLiteEventBus.getInstance().emitBulkUpdate(storeKey as AppTableName)
              }
              console.info(
                `[SyncManager] taskId ${taskId}: 병합 데이터 ${mergedData.length}개 SQLite 저장 완료 (기록 ${writtenCount}, 동일 스킵 ${skippedCount})`,
              )
            } catch (saveError) {
              console.error(`[SyncManager] ❌ taskId ${taskId}: SQLite 저장 실패:`, saveError)
            }

            // 6. modifiedFromLocal=1인 항목 필터링 (큐 추가용)
            const stillModifiedData = mergedData.filter(
              (item: SyncableDataType) => item.modifiedFromLocal,
            )

            // 데이터 맵에 저장
            storeDataMap.set(taskId, {
              mergedData,
              stillModifiedData,
            })
          }

          console.info(`[SyncManager] ${name} 병합 완료`)
        } catch (error) {
          console.error(`[SyncManager] ${name} 병합 중 오류:`, error)
          if (storeKey === 'defect_photos' || storeKey === 'facility_photos') {
            console.warn(
              `[PhotoGuard] ${name} merge error: ${error instanceof Error ? error.message : String(error)}`,
            )
          }
          continue
        }
      }

      console.info('[SyncManager] 2단계: 모든 store 큐 추가 시작')
      // 4단계: 병합 완료 후 큐에 추가
      for (const { name, store, storeKey } of storesToSync) {
        // 중단 확인
        if (this.checkAbort()) return

        try {
          // device_event_log는 서버 병합 없이 taskId별로 큐에 추가
          if (storeKey === 'device_event_log') {
            const taskIds = await this.getUniqueTaskIds(storeKey, true)
            if (taskIds.length === 0) {
              console.info(`[SyncManager] ${name}: 동기화할 데이터 없음`)
              continue
            }

            console.info(`[SyncManager] ${name}: ${taskIds.length}개 task 발견`)

            // taskId별로 로그 데이터 가져와서 큐에 추가
            for (const taskId of taskIds) {
              // 중단 확인
              if (this.checkAbort()) return

              const localData = await this.getLocalDataByTaskId(storeKey, taskId)
              const pendingData = localData.filter(
                (item: SyncableDataType) => item.modifiedFromLocal,
              )

              if (pendingData.length > 0) {
                console.info(
                  `[SyncManager] ${name} - taskId ${taskId}: ${pendingData.length}개 큐에 추가`,
                )
                await store.addApiQueue(pendingData)
              }
            }
            continue
          }

          // 병합된 데이터 가져오기
          const storeDataMap = mergedDataByStore.get(storeKey)
          if (!storeDataMap || storeDataMap.size === 0) {
            continue
          }

          // taskId별로 큐 추가
          for (const [taskId, { stillModifiedData }] of storeDataMap.entries()) {
            // 중단 확인
            if (this.checkAbort()) return

            if (stillModifiedData.length > 0) {
              console.info(
                `[PhotoGuard] ${name} task=${taskId}: pending=${stillModifiedData.length}`,
              )
            }
            console.info(
              `[SyncManager] ${name} - taskId ${taskId}: 미동기화 항목 ${stillModifiedData.length}개`,
            )

            if (stillModifiedData.length > 0) {
              const { itemsToProcess } = await getItemToProcess(stillModifiedData, storeKey)
              if (store.addApiQueue && itemsToProcess.length > 0) {
                console.info(
                  `[SyncManager] ${name} - taskId ${taskId}: ${itemsToProcess.length}개 큐에 추가`,
                )
                await store.addApiQueue(itemsToProcess)
              }
            }
          }
        } catch (error) {
          console.error(`[SyncManager] ${name} 큐 추가 중 오류:`, error)
          if (storeKey === 'defect_photos' || storeKey === 'facility_photos') {
            console.warn(
              `[PhotoGuard] ${name} enqueue error: ${error instanceof Error ? error.message : String(error)}`,
            )
          }
          continue
        }
      }

      // 5단계: 모든 동기화 완료 후 taskId들을 completed로 설정
      if (allTaskIds.size > 0) {
        console.info(`[SyncManager] ${allTaskIds.size}개 taskId를 completed 상태로 설정`)
        allTaskIds.forEach(taskId => this.setTaskSyncStatus(taskId, 'completed'))
      }
    } catch (error) {
      console.error('[SyncManager] ❌ 전체 동기화 중 오류 발생:', error)
    } finally {
      // auto-checkpoint를 꺼둔 상태이므로(사용자 커밋의 checkpoint 떠안기 방지)
      // 유휴 시점인 사이클 종료 시 PASSIVE checkpoint로 WAL을 정리한다 (블로킹 없음)
      await this.checkpointWal()
      this.releaseLock()
      const elapsedSec = ((performance.now() - syncCycleStartedAt) / 1000).toFixed(1)
      console.info(`[SyncManager] 전체 동기화 완료 (소요시간: ${elapsedSec}s)`)
    }
  }

  // WAL 백그라운드 정리 - PASSIVE는 활성 reader/writer를 기다리지 않고 가능한 만큼만 이동
  private static async checkpointWal(): Promise<void> {
    try {
      const db = await SQL.getDatabase()
      await db.query('PRAGMA wal_checkpoint(PASSIVE)')
    } catch (error) {
      console.warn('[SyncManager] WAL checkpoint 실패:', error)
    }
  }

  // merge 버그(0f3e0563)로 잘못 soft-delete된 _need_to_upload 레코드를 1회 복구
  private static async recoverBugDeletedRecords(): Promise<void> {
    const RECOVERY_FLAG = 'bug_recovery_merge_soft_delete_v1'
    if (localStorage.getItem(RECOVERY_FLAG)) return

    const db = await SQL.getDatabase()
    const tables: { name: string; parentCheck?: string }[] = [
      // defects 먼저 복구 (defect_photos 부모 검증에 필요)
      { name: 'defects' },
      {
        name: 'defect_photos',
        parentCheck: `AND EXISTS (
          SELECT 1 FROM defects d
          WHERE d.localStringId = defect_photos.localShapeId
            AND (d.deleted_at IS NULL OR d.deleted_at = '')
        )`,
      },
      { name: 'facility_photos' },
    ]

    const counts: string[] = []

    for (const { name, parentCheck } of tables) {
      try {
        const tableExists = SQL.createdTables.has(name)
        if (!tableExists) {
          counts.push(`${name} 0`)
          continue
        }

        const condition = `WHERE id LIKE '%_need_to_upload'
          AND deleted_at IS NOT NULL AND deleted_at != ''
          AND (fields_updated_at IS NULL OR fields_updated_at NOT LIKE '%"deleted_at"%')
          ${parentCheck || ''}`

        const selectResult = await db.query(`SELECT localStringId FROM ${name} ${condition}`)
        const affected = selectResult.values?.length || 0

        if (affected > 0) {
          await db.run(`UPDATE ${name} SET deleted_at = NULL, modifiedFromLocal = 1 ${condition}`)
          if (APP_TABLE_NAMES.includes(name as any)) {
            await SQLiteEventBus.getInstance().emitBulkUpdate(name as AppTableName)
          }
        }

        counts.push(`${name} ${affected}`)
      } catch (e) {
        console.error(`[SyncManager] Bug recovery (${name}) failed:`, e)
        counts.push(`${name} error`)
      }
    }

    localStorage.setItem(RECOVERY_FLAG, new Date().toISOString())
    console.info(`[SyncManager] Bug recovery completed: ${counts.join(', ')}`)
  }
}
